import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { getAvatarSrc, DEFAULT_AVATAR } from '../utils/avatar.ts';
import { API_BASE } from '../utils/apiEndpoints';
import { validateProfileForm, LIMITS } from '../utils/validation.ts';
import { useClickOutside } from '../hooks/useClickOutside.ts';
import { getSkillSuggestions } from '../config/skillSuggestions.ts';
import {
  JOB_TITLES,
  getJobTitleSuggestions,
  normalizeJobTitle,
} from '../config/jobTitles.ts';
import { WorkPeriodPicker } from '../components/WorkPeriodPicker.tsx';
import { NotificationSettings } from '../components/NotificationSettings.tsx';
import { AppearanceSettings } from '../components/AppearanceSettings.tsx';
import { LinkedAccountsSettings } from '../components/LinkedAccountsSettings.tsx';
import { AnticheatSettings } from '../components/AnticheatSettings.tsx';
import { PrivacySettings } from '../components/PrivacySettings.tsx';
import { AgreementSettings } from '../components/AgreementSettings.tsx';
import { ChangePasswordSettings } from '../components/ChangePasswordSettings.tsx';
import { ChangeEmailSettings } from '../components/ChangeEmailSettings.tsx';
import {
  loadProfileExtras,
  saveProfileExtras,
  createWorkHistoryRow,
  type ProfileExtras,
  type SocialNetworkId,
  type WorkHistoryRow,
} from '../utils/profileExtras.ts';
import './css/SettingsPage.css';

type SettingsSection = 'general' | 'privacy' | 'anticheat' | 'notifications' | 'appearance' | 'linked' | 'agreement';

type IntegrityTrustLevel = 'trusted' | 'suspicious' | 'cheater';

function normalizeIntegrityTrustLevel(raw: unknown): IntegrityTrustLevel {
  if (raw === 'suspicious' || raw === 'cheater') return raw;
  return 'trusted';
}

interface UserProfile {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  pending_email?: string | null;
  avatar: string;
  phone: string;
  birthday: string | null;
  job_title: string[];
  university: string;
  skills: string[];
  show_classes_section?: boolean;
  integrity_trust_level?: IntegrityTrustLevel;
  yandex_id?: string | null;
  vk_id?: string | null;
}

function SettingsPage() {
  const navigate = useNavigate();
  const { updateUser, isAuthenticated, user, loading: authLoading } = useAuth();
  const { t, currentLanguage } = useTranslation();
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
  const [skillDraft, setSkillDraft] = useState('');
  const [skillsSuggestOpen, setSkillsSuggestOpen] = useState(false);
  const [skillsHighlight, setSkillsHighlight] = useState(0);
  const skillsSuggestRef = useRef<HTMLDivElement>(null);
  const [jobTitleDraft, setJobTitleDraft] = useState('');
  const [jobTitleSuggestOpen, setJobTitleSuggestOpen] = useState(false);
  const [jobTitleHighlight, setJobTitleHighlight] = useState(0);
  const jobTitleSuggestRef = useRef<HTMLDivElement>(null);
  const [workHistory, setWorkHistory] = useState<WorkHistoryRow[]>([createWorkHistoryRow()]);
  const [specialty, setSpecialty] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [socials, setSocials] = useState<Partial<Record<SocialNetworkId, string>>>({});
  const [socialsMenuOpen, setSocialsMenuOpen] = useState(false);
  const socialsRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    document.title = t('settings.pageTitle');
    return () => { document.title = 'Sciplex'; };
  }, [t]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      const response = await fetch(`${API_BASE}/api/profile/profile`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const titles = Array.isArray(data.job_title)
        ? data.job_title
            .map((t: unknown) => (typeof t === 'string' ? normalizeJobTitle(t) : null))
            .filter((t: string | null): t is string => Boolean(t))
            .slice(0, 2)
        : [];
      setProfile({
        ...data,
        job_title: titles,
        integrity_trust_level: normalizeIntegrityTrustLevel(data.integrity_trust_level),
      });
      const extras = loadProfileExtras(Number(data.id));
      let history = extras.workHistory;
      if (
        history.length === 1
        && !history[0].workplace
        && !history[0].position
        && !history[0].startDate
        && titles.length > 0
      ) {
        history = [{ ...history[0], position: titles[0] }];
      }
      setWorkHistory(history);
      setSpecialty(extras.specialty);
      setGradYear(extras.gradYear);
      setSocials(extras.socials);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedLoadProfile'));
    } finally {
      setLoading(false);
    }
  };

  // Загрузка профиля после проверки авторизации
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [authLoading, isAuthenticated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => prev ? { ...prev, [name]: value === '' && name === 'birthday' ? null : value } : null);
  };

  const SKILLS_MAX = 20;
  const JOB_TITLES_MAX = 2;

  const closeSocialsMenu = useCallback(() => setSocialsMenuOpen(false), []);
  useClickOutside(socialsRef, closeSocialsMenu);
  const closeSkillsSuggest = useCallback(() => setSkillsSuggestOpen(false), []);
  useClickOutside(skillsSuggestRef, closeSkillsSuggest);
  const closeJobTitleSuggest = useCallback(() => setJobTitleSuggestOpen(false), []);
  useClickOutside(jobTitleSuggestRef, closeJobTitleSuggest);

  const skillSuggestions = profile
    ? getSkillSuggestions(skillDraft, profile.skills || [], 8)
    : [];

  const jobTitleSuggestions = profile
    ? getJobTitleSuggestions(jobTitleDraft, profile.job_title || [], 10)
    : [];

  const addJobTitle = (title: string) => {
    const normalized = normalizeJobTitle(title);
    if (!normalized || !profile) return;
    const list = profile.job_title || [];
    if (
      list.some((s) => s.toLowerCase() === normalized.toLowerCase())
      || list.length >= JOB_TITLES_MAX
    ) {
      setJobTitleDraft('');
      setJobTitleSuggestOpen(false);
      return;
    }
    setProfile({ ...profile, job_title: [...list, normalized] });
    setJobTitleDraft('');
    setJobTitleSuggestOpen(false);
    setJobTitleHighlight(0);
  };

  const removeJobTitle = (title: string) => {
    if (!profile) return;
    setProfile({
      ...profile,
      job_title: (profile.job_title || []).filter((t) => t !== title),
    });
  };

  const handleJobTitleDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setJobTitleSuggestOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (jobTitleSuggestions.length === 0) return;
      setJobTitleSuggestOpen(true);
      setJobTitleHighlight((i) => (i + 1) % jobTitleSuggestions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (jobTitleSuggestions.length === 0) return;
      setJobTitleSuggestOpen(true);
      setJobTitleHighlight((i) => (i - 1 + jobTitleSuggestions.length) % jobTitleSuggestions.length);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (jobTitleSuggestOpen && jobTitleSuggestions[jobTitleHighlight]) {
      addJobTitle(jobTitleSuggestions[jobTitleHighlight]);
    }
  };

  const addSkill = (skill: string) => {
    const value = skill.trim();
    if (!value || !profile) return;
    const list = profile.skills || [];
    if (list.some((s) => s.toLowerCase() === value.toLowerCase()) || list.length >= SKILLS_MAX) {
      setSkillDraft('');
      setSkillsSuggestOpen(false);
      return;
    }
    setProfile({ ...profile, skills: [...list, value] });
    setSkillDraft('');
    setSkillsSuggestOpen(false);
    setSkillsHighlight(0);
  };

  const addSocialNetwork = (id: SocialNetworkId) => {
    setSocials((prev) => (id in prev ? prev : { ...prev, [id]: '' }));
    setSocialsMenuOpen(false);
  };

  const updateSocialNickname = (id: SocialNetworkId, value: string) => {
    setSocials((prev) => ({ ...prev, [id]: value }));
  };

  const removeSocialNetwork = (id: SocialNetworkId) => {
    setSocials((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const availableSocialNetworks: { id: SocialNetworkId; label: string }[] = [
    { id: 'vk', label: t('settings.socialVk') },
    { id: 'max', label: t('settings.socialMax') },
  ].filter((item) => !(item.id in socials));

  const updateWorkHistoryRow = (id: string, patch: Partial<WorkHistoryRow>) => {
    setWorkHistory((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addWorkHistoryRow = () => {
    setWorkHistory((rows) => [...rows, createWorkHistoryRow()]);
  };

  const removeWorkHistoryRow = (id: string) => {
    setWorkHistory((rows) => (rows.length <= 1 ? rows : rows.filter((row) => row.id !== id)));
  };

  const handleWorkPeriodChange = (
    id: string,
    patch: { startDate: string; endDate: string; isCurrent: boolean }
  ) => {
    setWorkHistory((rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const handleSkillDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (skillSuggestions.length === 0) return;
      e.preventDefault();
      setSkillsSuggestOpen(true);
      setSkillsHighlight((i) => (i + 1) % skillSuggestions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      if (skillSuggestions.length === 0) return;
      e.preventDefault();
      setSkillsSuggestOpen(true);
      setSkillsHighlight((i) => (i - 1 + skillSuggestions.length) % skillSuggestions.length);
      return;
    }
    if (e.key === 'Escape') {
      setSkillsSuggestOpen(false);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (skillsSuggestOpen && skillSuggestions[skillsHighlight]) {
      addSkill(skillSuggestions[skillsHighlight]);
      return;
    }
    addSkill(skillDraft);
  };

  const handleSkillRemove = (skill: string) => {
    setProfile((prev) =>
      prev ? { ...prev, skills: (prev.skills || []).filter((s) => s !== skill) } : null
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    const jobTitlesForSave = (profile.job_title || [])
      .map((t) => normalizeJobTitle(t))
      .filter((t): t is string => Boolean(t))
      .slice(0, JOB_TITLES_MAX);

    const err = validateProfileForm({
      username: profile.username,
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone || null,
      job_title: jobTitlesForSave,
      university: profile.university || null,
      skills: profile.skills,
      birthday: profile.birthday || null,
    });
    if (err) {
      setError(err);
      return;
    }

    const normalizedWorkHistory = workHistory.map((row) => ({
      ...row,
      workplace: row.workplace.trim(),
      position: normalizeJobTitle(row.position) || '',
      startDate: row.startDate,
      endDate: row.isCurrent ? '' : row.endDate,
    }));

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      const response = await fetch(`${API_BASE}/api/profile/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: profile.username,
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          birthday: profile.birthday ? String(profile.birthday).slice(0, 10) : null,
          job_title: jobTitlesForSave,
          university: profile.university?.trim() || null,
          skills: profile.skills,
          avatar: profile.avatar
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const errMsg = data.error || data.message || t('settings.failedUpdateProfile');
        const translated =
          errMsg === 'registration.usernameAlreadyTaken' ? t('registration.usernameAlreadyTaken') : errMsg;
        throw new Error(translated);
      }

      saveProfileExtras(profile.id, {
        specialty: specialty.trim(),
        gradYear: gradYear.trim(),
        primaryDirection: '',
        directions: [],
        workHistory: normalizedWorkHistory,
        expertiseRows: loadProfileExtras(profile.id).expertiseRows,
        socials: Object.fromEntries(
          Object.entries(socials).map(([k, v]) => [k, (v ?? '').trim()])
        ) as ProfileExtras['socials'],
      });
      
      setSuccess(t('settings.profileUpdated'));
      setTimeout(() => {
        navigate('/profile'); // Перенаправление на страницу профиля
      }, 2000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.failedUpdateProfile'));
    } finally {
      setSaving(false);
    }
  };

  // Загрузка аватара на сервер (в БД сохраняется ссылка /uploads/avatars/...)
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAuthenticated) {
      setError(t('settings.authRequired'));
      return;
    }
    setUploadingAvatar(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await fetch(API_BASE + '/api/profile/avatar', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('settings.avatarUploadError'));

      const avatarUrl = data.avatar;
      setProfile(prev => prev ? { ...prev, avatar: avatarUrl } : null);
      updateUser?.({ avatar: avatarUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.avatarUploadError'));
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  if (authLoading) return <div className="loading">{t('settings.loading')}</div>;
  if (!isAuthenticated) return null;

  return (
    <div className="settings-page">
      <div className="settings-layout">
        <aside className="settings-sidebar">
          <nav className="settings-nav">
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'general' ? 'active' : ''}`}
              onClick={() => { setActiveSection('general'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionGeneral')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'privacy' ? 'active' : ''}`}
              onClick={() => { setActiveSection('privacy'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionPrivacy')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'anticheat' ? 'active' : ''}`}
              onClick={() => { setActiveSection('anticheat'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionAnticheat')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'notifications' ? 'active' : ''}`}
              onClick={() => { setActiveSection('notifications'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionNotifications')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'appearance' ? 'active' : ''}`}
              onClick={() => { setActiveSection('appearance'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionAppearance')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'linked' ? 'active' : ''}`}
              onClick={() => { setActiveSection('linked'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionLinkedAccounts')}
            </button>
            <button
              type="button"
              className={`settings-nav-item ${activeSection === 'agreement' ? 'active' : ''}`}
              onClick={() => { setActiveSection('agreement'); setError(null); setSuccess(null); }}
            >
              {t('settings.sectionAgreement')}
            </button>
          </nav>
        </aside>

        <div className="settings-main">
          {activeSection === 'general' && (
            <div className="settings-container">
              {loading ? (
                <div className="loading">{t('settings.loading')}</div>
              ) : !profile ? (
                <div className="error">{t('settings.noProfileData')}</div>
              ) : (
                <>
                  <h1 className="settings-title">{t('settings.sectionGeneral')}</h1>
                  <p className="settings-subtitle">{t('settings.generalIntro')}</p>
                  {error && <div className="alert alert-error">{error}</div>}
                  {success && <div className="alert alert-success">{success}</div>}
                  <form onSubmit={handleSubmit} className="settings-form">
                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.photoAndNickname')}</h2>
                      <div className="settings-identity">
                        <div className="settings-avatar-block">
                          <img
                            src={getAvatarSrc(profile.avatar)}
                            alt={t('settings.avatarAlt')}
                            className="settings-avatar-img"
                          />
                          <div className="settings-avatar-actions">
                            <label htmlFor="avatar-upload" className="btn btn-secondary" style={{ opacity: uploadingAvatar ? 0.6 : 1 }}>
                              {uploadingAvatar ? t('settings.uploading') : t('settings.uploadNewPhoto')}
                            </label>
                            <input
                              id="avatar-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              disabled={uploadingAvatar}
                              style={{ display: 'none' }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => setProfile((prev) => (prev ? { ...prev, avatar: DEFAULT_AVATAR } : null))}
                            >
                              {t('settings.remove')}
                            </button>
                          </div>
                        </div>

                        <div className="settings-identity-fields">
                          <div className="settings-field">
                            <label className="settings-field-label" htmlFor="username">{t('settings.nickname')}</label>
                            <input
                              type="text"
                              id="username"
                              name="username"
                              value={profile.username}
                              onChange={handleChange}
                              className="settings-input"
                              required
                              minLength={LIMITS.USERNAME_MIN}
                              maxLength={LIMITS.USERNAME_MAX}
                              title={`${LIMITS.USERNAME_MIN}-${LIMITS.USERNAME_MAX} ${t('settings.usernameTitle')}`}
                            />
                          </div>
                          <div className="settings-name-row">
                            <div className="settings-field">
                              <label className="settings-field-label" htmlFor="firstName">{t('settings.firstName')}</label>
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={profile.firstName}
                                onChange={handleChange}
                                className="settings-input"
                                maxLength={LIMITS.FIRST_NAME_MAX}
                              />
                            </div>
                            <div className="settings-field">
                              <label className="settings-field-label" htmlFor="lastName">{t('settings.lastName')}</label>
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={profile.lastName}
                                onChange={handleChange}
                                className="settings-input"
                                maxLength={LIMITS.LAST_NAME_MAX}
                              />
                            </div>
                          </div>

                          <div className="settings-skills-input-wrap">
                            <label className="settings-field-label" htmlFor="job-title-enter">
                              {t('settings.jobTitleLabel')}
                            </label>
                            <div className="settings-skills-combobox" ref={jobTitleSuggestRef}>
                              <input
                                id="job-title-enter"
                                type="text"
                                className="settings-input"
                                value={jobTitleDraft}
                                onChange={(e) => {
                                  setJobTitleDraft(e.target.value);
                                  setJobTitleSuggestOpen(true);
                                  setJobTitleHighlight(0);
                                }}
                                onFocus={() => setJobTitleSuggestOpen(true)}
                                onKeyDown={handleJobTitleDraftKeyDown}
                                placeholder={t('settings.jobTitleSearch')}
                                disabled={(profile.job_title?.length ?? 0) >= JOB_TITLES_MAX}
                                autoComplete="off"
                                role="combobox"
                                aria-expanded={jobTitleSuggestOpen && jobTitleSuggestions.length > 0}
                                aria-autocomplete="list"
                              />
                              {jobTitleSuggestOpen
                                && (profile.job_title?.length ?? 0) < JOB_TITLES_MAX
                                && jobTitleSuggestions.length > 0 && (
                                <ul className="settings-skills-suggest" role="listbox">
                                  {jobTitleSuggestions.map((title, idx) => (
                                    <li key={title}>
                                      <button
                                        type="button"
                                        className={`settings-skills-suggest-item${idx === jobTitleHighlight ? ' is-active' : ''}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => addJobTitle(title)}
                                        onMouseEnter={() => setJobTitleHighlight(idx)}
                                        role="option"
                                        aria-selected={idx === jobTitleHighlight}
                                      >
                                        {title}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            {(profile.job_title?.length ?? 0) > 0 && (
                              <div className="settings-skills-chips">
                                {profile.job_title.map((title) => (
                                  <span key={title} className="settings-skill-chip">
                                    {title}
                                    <button
                                      type="button"
                                      onClick={() => removeJobTitle(title)}
                                      aria-label={t('settings.remove')}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="settings-field-hint">{t('settings.jobTitleMaxHint')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.skillsAndExpertise')}</h2>
                      <div className="settings-skills-input-wrap">
                        <label className="settings-field-label" htmlFor="skills-enter">{t('settings.yourSkills')}</label>
                        <div className="settings-skills-combobox" ref={skillsSuggestRef}>
                          <input
                            id="skills-enter"
                            type="text"
                            className="settings-input"
                            value={skillDraft}
                            onChange={(e) => {
                              setSkillDraft(e.target.value);
                              setSkillsSuggestOpen(true);
                              setSkillsHighlight(0);
                            }}
                            onFocus={() => {
                              if (skillDraft.trim()) setSkillsSuggestOpen(true);
                            }}
                            onKeyDown={handleSkillDraftKeyDown}
                            placeholder={t('settings.skillsEnterPlaceholder')}
                            disabled={(profile.skills?.length ?? 0) >= SKILLS_MAX}
                            autoComplete="off"
                            role="combobox"
                            aria-expanded={skillsSuggestOpen && skillSuggestions.length > 0}
                            aria-autocomplete="list"
                          />
                          {skillsSuggestOpen && skillSuggestions.length > 0 && (
                            <ul className="settings-skills-suggest" role="listbox">
                              {skillSuggestions.map((skill, idx) => (
                                <li key={skill}>
                                  <button
                                    type="button"
                                    className={`settings-skills-suggest-item${idx === skillsHighlight ? ' is-active' : ''}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => addSkill(skill)}
                                    onMouseEnter={() => setSkillsHighlight(idx)}
                                    role="option"
                                    aria-selected={idx === skillsHighlight}
                                  >
                                    {skill}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {(profile.skills?.length ?? 0) > 0 && (
                          <div className="settings-skills-chips">
                            {profile.skills.map((skill) => (
                              <span key={skill} className="settings-skill-chip">
                                {skill}
                                <button type="button" onClick={() => handleSkillRemove(skill)} aria-label={t('settings.remove')}>
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.workInfo')}</h2>
                      <div className="settings-work-history">
                        {workHistory.map((row, index) => (
                          <div key={row.id} className="settings-work-row">
                            <div className="settings-field">
                              <label className="settings-field-label" htmlFor={`workplace-${row.id}`}>
                                {t('settings.workplace')}
                              </label>
                              <input
                                id={`workplace-${row.id}`}
                                type="text"
                                className="settings-input"
                                value={row.workplace}
                                onChange={(e) => updateWorkHistoryRow(row.id, { workplace: e.target.value })}
                                placeholder={t('settings.workplacePlaceholder')}
                                maxLength={120}
                              />
                            </div>
                            <div className="settings-field">
                              <label className="settings-field-label" htmlFor={`position-${row.id}`}>
                                {t('settings.position')}
                              </label>
                              <select
                                id={`position-${row.id}`}
                                className="settings-input"
                                value={
                                  normalizeJobTitle(row.position) || (row.position ? row.position : '')
                                }
                                onChange={(e) =>
                                  updateWorkHistoryRow(row.id, {
                                    position: normalizeJobTitle(e.target.value) || '',
                                  })
                                }
                              >
                                <option value="">{t('settings.jobTitleSelectPlaceholder')}</option>
                                {row.position
                                  && !normalizeJobTitle(row.position) && (
                                  <option value={row.position}>{row.position}</option>
                                )}
                                {JOB_TITLES.map((title) => (
                                  <option key={title} value={title}>
                                    {title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="settings-field">
                              <label className="settings-field-label" htmlFor={`work-period-${row.id}`}>
                                {t('settings.workPeriod')}
                              </label>
                              <div className="settings-work-period-wrap">
                                <WorkPeriodPicker
                                  id={`work-period-${row.id}`}
                                  value={{
                                    startDate: row.startDate,
                                    endDate: row.endDate,
                                    isCurrent: row.isCurrent,
                                  }}
                                  onChange={(patch) => handleWorkPeriodChange(row.id, patch)}
                                />
                                {index > 0 && (
                                  <button
                                    type="button"
                                    className="settings-work-row__remove"
                                    onClick={() => removeWorkHistoryRow(row.id)}
                                    aria-label={t('settings.remove')}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button type="button" className="settings-expertise-add" onClick={addWorkHistoryRow}>
                        {t('settings.addWorkExperience')}
                      </button>
                    </div>

                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.education')}</h2>
                      <div className="settings-grid-3">
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="university">{t('settings.universityLabel')}</label>
                          <input
                            type="text"
                            id="university"
                            name="university"
                            value={profile.university || ''}
                            onChange={handleChange}
                            className="settings-input"
                            placeholder={t('settings.universityPlaceholder')}
                            maxLength={LIMITS.UNIVERSITY_MAX}
                          />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="specialty">{t('settings.specialty')}</label>
                          <input
                            id="specialty"
                            className="settings-input"
                            value={specialty}
                            onChange={(e) => setSpecialty(e.target.value)}
                            placeholder={t('settings.specialtyPlaceholder')}
                            maxLength={120}
                          />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="gradYear">{t('settings.gradYear')}</label>
                          <input
                            id="gradYear"
                            className="settings-input"
                            value={gradYear}
                            onChange={(e) => setGradYear(e.target.value)}
                            placeholder={t('settings.gradYearPlaceholder')}
                            maxLength={20}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.contactInfo')}</h2>
                      <div className="settings-grid-3">
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="email">{t('settings.email')}</label>
                          <input
                            type="email"
                            id="email"
                            value={profile.email}
                            className="settings-input"
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="phone">{t('settings.phoneNumber')}</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={profile.phone || ''}
                            onChange={handleChange}
                            className="settings-input"
                            placeholder={t('settings.phonePlaceholder')}
                            maxLength={LIMITS.PHONE_MAX}
                          />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="socials">{t('settings.socialNetworks')}</label>
                          <div className="settings-socials" ref={socialsRef}>
                            <div
                              className={`settings-socials-box${Object.keys(socials).length === 0 ? ' settings-socials-box--empty' : ''}`}
                              onClick={() => {
                                if (availableSocialNetworks.length > 0) setSocialsMenuOpen((o) => !o);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (availableSocialNetworks.length > 0) setSocialsMenuOpen((o) => !o);
                                }
                              }}
                              aria-haspopup="listbox"
                              aria-expanded={socialsMenuOpen}
                              id="socials"
                            >
                              {Object.keys(socials).length === 0 ? (
                                <span className="settings-socials-placeholder">{t('settings.socialsPlaceholder')}</span>
                              ) : (
                                <div className="settings-socials-list" onClick={(e) => e.stopPropagation()}>
                                  {(Object.keys(socials) as SocialNetworkId[]).map((id) => (
                                    <div key={id} className="settings-socials-row">
                                      <span className="settings-socials-network">
                                        {id === 'vk' ? t('settings.socialVk') : t('settings.socialMax')}
                                      </span>
                                      <input
                                        type="text"
                                        className="settings-socials-nick"
                                        value={socials[id] ?? ''}
                                        onChange={(e) => updateSocialNickname(id, e.target.value)}
                                        placeholder={t('settings.socialNickPlaceholder')}
                                        maxLength={64}
                                      />
                                      <button
                                        type="button"
                                        className="settings-socials-remove"
                                        onClick={() => removeSocialNetwork(id)}
                                        aria-label={t('settings.remove')}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                  {availableSocialNetworks.length > 0 && (
                                    <button
                                      type="button"
                                      className="settings-socials-add-more"
                                      onClick={() => setSocialsMenuOpen(true)}
                                    >
                                      + {t('settings.socialsAdd')}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            {socialsMenuOpen && availableSocialNetworks.length > 0 && (
                              <ul className="settings-socials-menu" role="listbox">
                                {availableSocialNetworks.map((item) => (
                                  <li key={item.id}>
                                    <button
                                      type="button"
                                      className="settings-socials-menu-item"
                                      onClick={() => addSocialNetwork(item.id)}
                                    >
                                      {item.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <h2 className="section-title">{t('settings.personalData')}</h2>
                      <div className="settings-grid-3">
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="birthday">{t('settings.dateOfBirth')}</label>
                          <input
                            type="date"
                            id="birthday"
                            name="birthday"
                            value={profile.birthday ? String(profile.birthday).slice(0, 10) : ''}
                            onChange={handleChange}
                            className="settings-input birthday-date-input"
                            max={new Date().toISOString().slice(0, 10)}
                            style={{ colorScheme: 'dark' }}
                          />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="timezone">{t('settings.timezone')}</label>
                          <input id="timezone" className="settings-input" disabled placeholder="—" title={t('settings.fieldSoon')} />
                        </div>
                        <div className="settings-field">
                          <label className="settings-field-label" htmlFor="city">{t('settings.countryCity')}</label>
                          <input id="city" className="settings-input" disabled placeholder="—" title={t('settings.fieldSoon')} />
                        </div>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/profile')}
                        disabled={saving}
                      >
                        {t('settings.cancel')}
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? t('settings.saving') : t('settings.saveChanges')}
                      </button>
                    </div>
                  </form>
                  <ChangeEmailSettings
                    currentEmail={profile.email}
                    pendingEmail={profile.pending_email || null}
                    onPendingChange={(pending) =>
                      setProfile((prev) => (prev ? { ...prev, pending_email: pending } : null))
                    }
                    t={t}
                    currentLanguage={currentLanguage}
                  />
                  <ChangePasswordSettings t={t} />
                </>
              )}
            </div>
          )}
          {activeSection === 'privacy' && user?.id != null && (
            loading ? (
              <div className="settings-container">
                <div className="loading">{t('settings.loading')}</div>
              </div>
            ) : !profile ? (
              <div className="settings-container">
                <div className="error">{t('settings.noProfileData')}</div>
              </div>
            ) : (
              <PrivacySettings
                email={profile.email}
                oauthLinked={Boolean(profile.yandex_id || profile.vk_id)}
                t={t}
              />
            )
          )}

          {activeSection === 'anticheat' && user?.id != null && (
            loading ? (
              <div className="settings-container">
                <div className="loading">{t('settings.loading')}</div>
              </div>
            ) : !profile ? (
              <div className="settings-container">
                <div className="error">{t('settings.noProfileData')}</div>
              </div>
            ) : (
              <AnticheatSettings
                trustLevel={normalizeIntegrityTrustLevel(profile.integrity_trust_level)}
                t={t}
              />
            )
          )}

          {activeSection === 'notifications' && user?.id != null && (
            <NotificationSettings userId={Number(user.id)} t={t} />
          )}

          {activeSection === 'appearance' && user?.id != null && (
            <AppearanceSettings userId={Number(user.id)} t={t} />
          )}

          {activeSection === 'linked' && user?.id != null && (
            <LinkedAccountsSettings
              userId={Number(user.id)}
              t={t}
              onCancel={() => setActiveSection('general')}
            />
          )}

          {activeSection === 'agreement' && user?.id != null && (
            <AgreementSettings t={t} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;